import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AdminShell from "@/pages/admin/AdminShell";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  UserRound,
  FileText,
  FileSpreadsheet,
  Mail,
  Trash2,
  Plus,
  History,
} from "lucide-react";

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AdminStatements() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialUserId = params.get("user_id") || "";
  const initialUserEmail = params.get("user_email") || "";
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState(initialUserEmail);
  const [pickedUser, setPickedUser] = useState(null);

  useEffect(() => {
    if (initialUserId) {
      api
        .get(`/admin/users/${initialUserId}`)
        .then(({ data }) => setPickedUser(data))
        .catch(() => {});
    }
  }, [initialUserId]);

  if (user === undefined || user === null) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;

  const runSearch = async () => {
    try {
      const { data } = await api.get(`/admin/users?search=${encodeURIComponent(search)}&limit=10`);
      setUsers(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AdminShell>
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-8">
        <div className="text-eyebrow text-[#4F4D45]">Support tooling</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1" data-testid="admin-statements-heading">
          Generate customer statement
        </h1>
        <p className="text-sm text-[#4F4D45] mt-2 max-w-2xl">
          Pick a user, edit the statement (add notes, adjust lines, exclude transactions), then download or email
          the signed PDF directly to them. Every action is written to the audit trail.
        </p>

        {!pickedUser ? (
          <div className="mt-8 fv-card p-6 md:p-8">
            <Label className="text-eyebrow text-[#4F4D45]">Find a user</Label>
            <div className="mt-2 flex gap-2 max-w-xl">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder="Search by name, email, wallet ID, or account number"
                data-testid="admin-statement-user-search"
              />
              <Button
                onClick={runSearch}
                className="bg-[#1B3526] hover:bg-[#0F2517] text-white"
                data-testid="admin-statement-user-search-btn"
              >
                <Search className="w-4 h-4 mr-2" /> Search
              </Button>
            </div>
            {users.length > 0 && (
              <div className="mt-5 divide-y divide-[#D2CDBE] border border-[#D2CDBE] rounded-xl overflow-hidden">
                {users.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setPickedUser(u)}
                    data-testid={`admin-statement-user-${u.id}`}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#E3DECF]"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#A85943] text-white flex items-center justify-center font-semibold">
                      {(u.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{u.name}</div>
                      <div className="text-xs text-[#4F4D45] truncate">
                        {u.email} · Acc {u.account_number || "—"} · {u.banking_mode}
                      </div>
                    </div>
                    <div className="text-xs font-mono text-[#4F4D45]">${(u.balance_demo || 0).toFixed(2)} / ${(u.balance_live || 0).toFixed(2)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <StatementEditor
            targetUser={pickedUser}
            onReset={() => setPickedUser(null)}
          />
        )}

        <HistorySection userId={pickedUser?.id} />
      </div>
    </AdminShell>
  );
}

function StatementEditor({ targetUser, onReset }) {
  const today = useMemo(() => new Date(), []);
  const default30 = useMemo(() => new Date(Date.now() - 30 * 86400_000), []);
  const [fromDate, setFromDate] = useState(toISO(default30));
  const [toDate, setToDate] = useState(toISO(today));
  const [mode, setMode] = useState(targetUser?.banking_mode || "all");
  const [note, setNote] = useState("Reissued at customer's request.");
  const [customLines, setCustomLines] = useState([]);
  const [excluded, setExcluded] = useState({});
  const [overrideOpening, setOverrideOpening] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const buildBody = useCallback(() => {
    const body = {
      from_date: fromDate,
      to_date: toDate,
      mode: mode === "all" ? "all" : mode,
      note: note?.trim() || null,
      custom_lines: customLines
        .filter((c) => c.description?.trim() && !isNaN(parseFloat(c.amount)))
        .map((c) => ({
          description: c.description.trim(),
          amount: parseFloat(c.amount),
          currency: c.currency || "USD",
          tag: c.tag || null,
        })),
      excluded_tx_ids: Object.keys(excluded).filter((k) => excluded[k]),
    };
    if (overrideOpening !== "" && !isNaN(parseFloat(overrideOpening))) {
      body.override_opening = parseFloat(overrideOpening);
    }
    return body;
  }, [fromDate, toDate, mode, note, customLines, excluded, overrideOpening]);

  const refreshPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/users/${targetUser.id}/statement/preview`,
        buildBody()
      );
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadFile = async (format) => {
    setDownloading(format);
    try {
      const res = await fetch(
        `${API}/admin/users/${targetUser.id}/statement?format=${format}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...buildBody(), send_email: false }),
        }
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FinVault-Statement-${targetUser.account_number || "user"}-${fromDate}_to_${toDate}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded (audit-logged).`);
    } catch (e) {
      toast.error(e?.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  const sendEmail = async () => {
    if (!window.confirm(`Send this statement to ${targetUser.email}?`)) return;
    setEmailing(true);
    try {
      const { data } = await api.post(
        `/admin/users/${targetUser.id}/statement`,
        { ...buildBody(), send_email: true }
      );
      if (data.email_sent) {
        toast.success(`Statement emailed to ${targetUser.email}.`);
      } else {
        toast.warning("Statement generated + logged, but email delivery failed (check Resend).");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEmailing(false);
    }
  };

  const money = (v) =>
    (v < 0 ? "-" : "") +
    "$" +
    Math.abs(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6" data-testid="admin-statement-editor">
      {/* Left: editor */}
      <div className="fv-card p-6 space-y-5 h-fit">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserRound className="w-4 h-4 text-[#264C39]" />
            <div>
              <div className="font-semibold text-sm">{targetUser.name}</div>
              <div className="text-xs text-[#4F4D45]">{targetUser.email}</div>
            </div>
          </div>
          <Button
            onClick={onReset}
            variant="outline"
            size="sm"
            className="border-[#D2CDBE] text-[#0A0A08]"
            data-testid="admin-statement-reset"
          >
            Change
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-[#4F4D45]">From</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="admin-statement-from"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-[#4F4D45]">To</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="admin-statement-to"
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs text-[#4F4D45]">Mode</Label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            data-testid="admin-statement-mode"
            className="mt-1 w-full h-10 px-3 rounded-md border border-[#D2CDBE] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3526]"
          >
            <option value="all">All (Live + Demo)</option>
            <option value="live">Live only</option>
            <option value="demo">Demo only</option>
          </select>
        </div>

        <div>
          <Label className="text-xs text-[#4F4D45]">Admin note (shown at top of PDF)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            data-testid="admin-statement-note"
            className="mt-1"
            placeholder="e.g. Reissued at customer request per ticket #1234."
          />
        </div>

        <div>
          <Label className="text-xs text-[#4F4D45]">Override opening balance (optional)</Label>
          <Input
            value={overrideOpening}
            onChange={(e) => setOverrideOpening(e.target.value)}
            placeholder="Leave blank to auto-compute"
            data-testid="admin-statement-override-opening"
            className="mt-1"
          />
        </div>

        {/* Custom lines editor */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-[#4F4D45]">Custom line items</Label>
            <Button
              onClick={() =>
                setCustomLines((cl) => [...cl, { description: "", amount: "", currency: "USD" }])
              }
              variant="outline"
              size="sm"
              className="border-[#D2CDBE] text-[#0A0A08] h-7"
              data-testid="admin-statement-add-line"
            >
              <Plus className="w-3 h-3 mr-1" /> Add line
            </Button>
          </div>
          {customLines.length === 0 ? (
            <div className="text-xs text-[#4F4D45] mt-2">No custom lines. Use positive amounts for credits, negative for debits.</div>
          ) : (
            <div className="mt-2 space-y-2">
              {customLines.map((cl, idx) => (
                <div key={idx} className="flex gap-2 items-start" data-testid={`admin-statement-line-${idx}`}>
                  <Input
                    value={cl.description}
                    onChange={(e) => {
                      const copy = [...customLines];
                      copy[idx] = { ...copy[idx], description: e.target.value };
                      setCustomLines(copy);
                    }}
                    placeholder="Description"
                    className="flex-1 text-sm"
                  />
                  <Input
                    value={cl.amount}
                    onChange={(e) => {
                      const copy = [...customLines];
                      copy[idx] = { ...copy[idx], amount: e.target.value };
                      setCustomLines(copy);
                    }}
                    placeholder="±0.00"
                    className="w-24 text-sm font-mono"
                    inputMode="decimal"
                  />
                  <Button
                    onClick={() => setCustomLines((cl2) => cl2.filter((_, i) => i !== idx))}
                    variant="outline"
                    size="icon"
                    className="border-[#D2CDBE] text-[#992F2F] h-9 w-9"
                    aria-label="Remove line"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-[#D2CDBE] space-y-2">
          <Button
            onClick={refreshPreview}
            disabled={busy}
            variant="outline"
            className="w-full border-[#D2CDBE] text-[#0A0A08]"
            data-testid="admin-statement-refresh"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Refresh preview
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => downloadFile("pdf")}
              disabled={downloading === "pdf"}
              className="bg-[#1B3526] hover:bg-[#0F2517] text-white"
              data-testid="admin-statement-download-pdf"
            >
              {downloading === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
              PDF
            </Button>
            <Button
              onClick={() => downloadFile("csv")}
              disabled={downloading === "csv"}
              variant="outline"
              className="border-[#D2CDBE] text-[#0A0A08]"
              data-testid="admin-statement-download-csv"
            >
              {downloading === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
              CSV
            </Button>
          </div>
          <Button
            onClick={sendEmail}
            disabled={emailing}
            className="w-full bg-[#B58957] hover:bg-[#a3784a] text-[#0A130E]"
            data-testid="admin-statement-send-email"
          >
            {emailing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Send to {targetUser.email}
          </Button>
        </div>
      </div>

      {/* Right: preview */}
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-3">
          <SumCell label="Opening" value={money(preview?.opening ?? 0)} testid="admin-sum-opening" />
          <SumCell label="Money in" value={"+" + money(preview?.money_in ?? 0)} color="text-[#4E7F5F]" testid="admin-sum-in" />
          <SumCell label="Money out" value={"-" + money(preview?.money_out ?? 0)} color="text-[#B85847]" testid="admin-sum-out" />
          <SumCell label="Closing" value={money(preview?.closing ?? 0)} testid="admin-sum-closing" />
        </div>
        <div className="fv-card overflow-hidden">
          <div className="px-5 py-3 border-b border-[#D2CDBE] text-sm text-[#4F4D45]">
            {preview
              ? `${preview.count} rows · toggle checkboxes to exclude`
              : "Loading…"}
          </div>
          {!preview || preview.count === 0 ? (
            <div className="p-10 text-center text-[#4F4D45]">No rows for this range.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left bg-[#F0ECE0]/70 text-[#4F4D45] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 w-8">Keep</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Type / Description</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((it) => {
                    const isAdj = it.type === "admin_adjustment";
                    return (
                      <tr key={it.tx_id} className="border-t border-[#D2CDBE]/60">
                        <td className="px-3 py-2">
                          {isAdj ? (
                            <span className="text-xs text-[#B58957] font-bold">ADJ</span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={!excluded[it.tx_id]}
                              onChange={(e) => {
                                setExcluded((prev) => ({ ...prev, [it.tx_id]: !e.target.checked }));
                                setTimeout(refreshPreview, 60);
                              }}
                              data-testid={`admin-statement-include-${it.tx_id}`}
                            />
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                          {it.created_at ? new Date(it.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-sm capitalize">{(it.type || "").replace("_", " ")}</div>
                          <div className="text-xs text-[#4F4D45] truncate max-w-md">{it.description}</div>
                        </td>
                        <td className={`px-3 py-2 text-right font-mono ${it.amount >= 0 ? "text-[#4E7F5F]" : "text-[#B85847]"}`}>
                          {it.amount >= 0 ? "+" : "-"}${Math.abs(it.amount).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">${(it.running || 0).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SumCell({ label, value, color = "text-[#0A0A08]", testid }) {
  return (
    <div className="fv-card p-4" data-testid={testid}>
      <div className="text-eyebrow text-[#4F4D45]">{label}</div>
      <div className={`font-display text-xl font-bold mt-1 tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

function HistorySection({ userId }) {
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const q = userId ? `?user_id=${userId}&limit=15` : `?limit=15`;
      const { data } = await api.get(`/admin/statement-history${q}`);
      setItems(data.history || []);
    } catch (e) {
      /* ignore */
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!items || items.length === 0) return null;
  return (
    <div className="mt-10 fv-card p-6" data-testid="admin-statement-history">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-4 h-4 text-[#264C39]" />
        <h2 className="font-display text-lg font-semibold">
          Audit log {userId ? "· this user" : "· all admin-issued statements"}
        </h2>
      </div>
      <div className="divide-y divide-[#D2CDBE]">
        {items.map((h) => (
          <div key={h.id} className="py-3 flex items-center justify-between gap-4 text-sm">
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">
                {h.user_name} <span className="text-[#4F4D45] font-normal">· {h.user_email}</span>
              </div>
              <div className="text-xs text-[#4F4D45] truncate mt-0.5">
                {h.from_date} → {h.to_date} · {h.count} rows · Opening ${(h.opening || 0).toFixed(2)} · Closing ${(h.closing || 0).toFixed(2)}
                {h.note ? ` · "${h.note}"` : ""}
              </div>
            </div>
            <div className="text-xs text-[#4F4D45] whitespace-nowrap">
              by <span className="font-mono">{h.admin_email}</span>
              <div className="text-right">
                {h.created_at ? new Date(h.created_at).toLocaleString() : "—"}
              </div>
              {h.emailed && (
                <span className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-[#4E7F5F]/10 text-[#264C39] font-medium">
                  {h.email_delivered ? "Emailed" : "Email failed"}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
